// src/pages/Investors.tsx — the people, companies and funds that hold shares in owning companies (Task K, prototype §33):
// a list, and one investor's record (contact, holdings across companies, statements). An Investor login sees only their own.
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Field, Input, KeyValue, Modal, Money, PageHeader, Select, Textarea } from '../components/ui';
import { DataTable, type Column } from '../components/ui/DataTable';
import { FormError, FormGrid, Wide } from '../components/forms/common';

import { formatDate } from '../lib/viz';
import { StatementsPanel } from '../components/owners';
import { ownersApi, INVESTOR_KIND_LABEL, type Investor, type InvestorKind } from '../lib/owners';
import { PhoneInput } from '../components/ui/PhoneInput';
import { formatPhone } from '../lib/phone';

export default function InvestorsPage() {
  const { id } = useParams<{ id?: string }>();
  return id ? <InvestorRecordPage id={id} /> : <InvestorList />;
}

function InvestorList() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('');
  const [adding, setAdding] = useState(false);
  const list = useQuery({ queryKey: ['/investors', q, kind], queryFn: () => ownersApi.investors({ q: q || undefined, kind: kind || undefined }) });
  const columns: Column<Investor>[] = [
    { key: 'name', label: 'Investor', render: (i) => <div><div className="font-medium text-ink-900">{i.name}</div><div className="text-[11.5px] text-ink-500">{INVESTOR_KIND_LABEL[i.kind]}{i.userId ? ' · can sign in' : ''}</div></div> },
    { key: 'companies', label: 'Holds', render: (i) => i.companies.length ? <div className="space-y-0.5">{i.companies.map((c) => <div key={c.entityId} className="text-[12.5px]"><span className="font-medium">{c.sharePct}%</span> <span className="text-ink-500">{c.entityName}</span></div>)}</div> : <span className="text-ink-400">Nothing now</span> },
    { key: 'capitalMinor', label: 'Put in', align: 'right', hideOnMobile: true, render: (i) => i.capitalMinor ? <Money minor={i.capitalMinor} /> : '—' },
    { key: 'email', label: 'Contact', hideOnMobile: true, render: (i) => <div className="text-[12.5px] text-ink-600">{i.email ?? <span className="text-amber-700">No email</span>}<div className="text-ink-400">{formatPhone(i.phone)}</div></div> },
  ];
  return (
    <div>
      <PageHeader title="Investors" menu={[{ label: 'Add investor', icon: 'Plus', can: 'investors.create', onClick: () => setAdding(true) }]} />
      <DataTable exportCsv={{ path: '/investors', page: 'investors' }} columns={columns} rows={list.data ?? []} page={1} onPage={() => {}} isLoading={list.isLoading} rowKey={(i) => i.id} onRowClick={(i) => navigate(`/app/investors/${i.id}`)}
        search={q} onSearch={setQ} searchPlaceholder="Name or email…" emptyTitle="No investors" emptyBody="Add the people and companies that hold shares in an owning company."
        filters={<Select value={kind} onChange={(e) => setKind(e.target.value)}><option value="">Every kind</option>{Object.entries(INVESTOR_KIND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</Select>} />
      {adding && <InvestorForm onClose={() => setAdding(false)} onSaved={(i) => navigate(`/app/investors/${i.id}`)} />}
    </div>
  );
}

function InvestorRecordPage({ id }: { id: string }) {
  const r = useQuery({ queryKey: ['/investors', id], queryFn: () => ownersApi.investor(id) });
  const [editing, setEditing] = useState(false);
  const i = r.data;
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="mx-auto max-w-6xl space-y-2.5">
      <PageHeader title={i?.name ?? 'Investor'} eyebrow={i ? INVESTOR_KIND_LABEL[i.kind] : undefined} back="/app/investors" menu={[!!i && { label: 'Edit investor', icon: 'Pencil', can: 'investors.update', onClick: () => setEditing(true) }]} />
      {r.error && <FormError error={r.error} />}
      {i && (
        <div className="grid gap-2.5 lg:grid-cols-[1fr_1.4fr]">
          <Card title="Contact">
            <KeyValue items={[
              { label: 'Email', value: i.email ?? <span className="text-amber-700">None — statements cannot be emailed</span> }, { label: 'Phone', value: formatPhone(i.phone) },
              { label: 'Company / fund no.', value: i.companyNumber }, { label: 'Address', value: [i.address?.line1, i.address?.city, i.address?.postcode].filter(Boolean).join(', ') || '—' },
              { label: 'Sign-in', value: i.userEmail ? `Yes — ${i.userEmail}` : 'No login' }, { label: 'Notes', value: i.notes },
            ]} />
          </Card>
          <Card title="Holdings" subtitle="Every company they hold, or have held, shares in">
            <ul className="divide-y divide-ink-100">
              {i.holdings.map((h) => {
                const live = h.fromDate <= today && (!h.toDate || h.toDate >= today);
                return (
                  <li key={h.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-[13px]">
                    <div className="min-w-0">
                      <Link to={`/app/entities/${h.entityId}?tab=ownership`} className="font-medium text-ink-900 hover:underline">{h.entityName}</Link>
                      <div className="text-[12px] text-ink-500">{formatDate(h.fromDate)} – {h.toDate ? formatDate(h.toDate) : 'now'}{h.capitalMinor ? <> · put in <Money minor={h.capitalMinor} currency={h.capitalCurrency} /></> : null}</div>
                    </div>
                    <div className="flex items-center gap-2"><span className="text-[15px] font-semibold">{h.sharePct}%</span><Badge value={live ? 'Holding' : h.fromDate > today ? 'Starts later' : 'Ended'} tone={live ? 'good' : 'neutral'} /></div>
                  </li>
                );
              })}
              {!i.holdings.length && <li className="py-3 text-[13px] text-ink-500">No holdings yet — add them from the company's Ownership tab.</li>}
            </ul>
          </Card>
          <div className="lg:col-span-2">
            <h3 className="mb-2 text-[13px] font-semibold text-ink-900">Statements</h3>
            <StatementsPanel entityIds={[...new Set(i.holdings.map((h) => h.entityId))]} showCompany />
          </div>
        </div>
      )}
      {editing && i && <InvestorForm investor={i} onClose={() => setEditing(false)} />}
    </div>
  );
}

function InvestorForm({ investor, onClose, onSaved }: { investor?: Investor; onClose: () => void; onSaved?: (i: Investor) => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    name: investor?.name ?? '', kind: (investor?.kind ?? 'INDIVIDUAL') as InvestorKind, email: investor?.email ?? '', phone: investor?.phone ?? '', companyNumber: investor?.companyNumber ?? '',
    line1: investor?.address?.line1 ?? '', city: investor?.address?.city ?? '', postcode: investor?.address?.postcode ?? '', notes: investor?.notes ?? '',
  });
  const save = useMutation({
    mutationFn: () => {
      const body = { name: f.name, kind: f.kind, email: f.email || null, phone: f.phone || null, companyNumber: f.companyNumber || null, notes: f.notes || null, address: { line1: f.line1 || undefined, city: f.city || undefined, postcode: f.postcode || undefined } };
      return investor ? ownersApi.updateInvestor(investor.id, body) : ownersApi.createInvestor(body);
    },
    onSuccess: (i) => { void qc.invalidateQueries({ queryKey: ['/investors'] }); void qc.invalidateQueries({ queryKey: ['owners'] }); onClose(); onSaved?.(i); },
  });
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal open onClose={onClose} title={investor ? `Edit ${investor.name}` : 'Add an investor'}
      footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={() => save.mutate()} disabled={!f.name.trim() || save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</Button></>}>
      <FormGrid>
        <Wide><Field label="Name"><Input value={f.name} onChange={set('name')} className="w-full" /></Field></Wide>
        <Field label="Kind"><Select value={f.kind} onChange={set('kind')} className="w-full">{Object.entries(INVESTOR_KIND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</Select></Field>
        <Field label="Company / fund no."><Input value={f.companyNumber} onChange={set('companyNumber')} className="w-full" /></Field>
        <Field label="Email (statements go here)"><Input type="email" value={f.email} onChange={set('email')} className="w-full" /></Field>
        <Field label="Phone"><PhoneInput autoComplete="off" value={f.phone} onChange={(phone) => setF({ ...f, phone })} /></Field>
        <Wide><Field label="Address"><Input value={f.line1} onChange={set('line1')} className="w-full" placeholder="Street" /></Field></Wide>
        <Field label="City"><Input value={f.city} onChange={set('city')} className="w-full" /></Field>
        <Field label="Postcode"><Input value={f.postcode} onChange={set('postcode')} className="w-full" /></Field>
        <Wide><Field label="Notes"><Textarea rows={2} value={f.notes} onChange={set('notes')} className="w-full" /></Field></Wide>
      </FormGrid>
      <div className="mt-3"><FormError error={save.error} /></div>
    </Modal>
  );
}
